/**
 * Volatile VGS CVV alias captured at card alta.
 * Memory only — never localStorage, sessionStorage, or Zustand persist.
 * A full reload asks for CVV again. 60 minutes is the safe floor versus a
 * VGS alias TTL of 1 hour or 24 hours.
 */
const aliases = new Map<string, { token: string; capturedAt: number }>();

export const FRESH_CVV_TTL_MS = 60 * 60 * 1000;

export const rememberFreshCvv = (
  paymentMethodId: string,
  cvvToken: string,
  capturedAt = Date.now(),
): void => {
  if (!paymentMethodId || !cvvToken) return;
  aliases.set(paymentMethodId, { token: cvvToken, capturedAt });
};

/** Returns the alias while it is younger than 60 minutes. Drops it otherwise. */
export const peekFreshCvv = (
  paymentMethodId: string,
  at = Date.now(),
): string | undefined => {
  const entry = aliases.get(paymentMethodId);
  if (!entry) return undefined;
  if (at - entry.capturedAt >= FRESH_CVV_TTL_MS) {
    aliases.delete(paymentMethodId);
    return undefined;
  }
  return entry.token;
};

export const forgetFreshCvv = (paymentMethodId: string): void => {
  aliases.delete(paymentMethodId);
};

export const clearFreshCvvAliases = (): void => {
  aliases.clear();
  authorizeAliases.clear();
};

/**
 * CVV alias typed on the confirm when usage-readiness asked for it (48 h rule
 * for SENDER cards without stored KYC). CVS ignores it on provision of an
 * already provisioned rail; it must travel as `fundingCvvToken` on
 * POST /operations so the authorize can send it. Same memory-only rules.
 */
const authorizeAliases = new Map<string, { token: string; capturedAt: number }>();

export const rememberAuthorizeCvv = (
  paymentMethodId: string,
  cvvToken: string,
  capturedAt = Date.now(),
): void => {
  if (!paymentMethodId || !cvvToken) return;
  authorizeAliases.set(paymentMethodId, { token: cvvToken, capturedAt });
};

export const peekAuthorizeCvv = (
  paymentMethodId: string,
  at = Date.now(),
): string | undefined => {
  const entry = authorizeAliases.get(paymentMethodId);
  if (!entry) return undefined;
  if (at - entry.capturedAt >= FRESH_CVV_TTL_MS) {
    authorizeAliases.delete(paymentMethodId);
    return undefined;
  }
  return entry.token;
};

export const forgetAuthorizeCvv = (paymentMethodId: string): void => {
  authorizeAliases.delete(paymentMethodId);
};
